import { FRAME_DURATION, lastCompleteFrameTime, snapToExportFrame } from '@/lib/timeline/exportFrameTiming';
import { EFFECT_SECTION_KNOBS, isDynamicEffect, type EffectId, type EffectSection } from './sections';

export const EFFECT_LANE_HEIGHT = 20;
const DEFAULT_SECTION_SECONDS = 2;

/** Sections that can render: those starting before the clip end. A section at
 *  or past the end never plays and is not drawn in Full-video view, so it must
 *  neither reserve a lane row nor gate export. Without a finite positive clip
 *  duration nothing is filtered. */
export function sectionsWithinClip<S extends Pick<EffectSection, 'start'>>(sections: ReadonlyArray<S>, clipDuration: number | undefined): S[] {
  if (!(typeof clipDuration === 'number' && Number.isFinite(clipDuration) && clipDuration > 0)) return [...sections];
  return sections.filter((s) => s.start < clipDuration);
}

/** Display rows the lane needs for these sections. */
export function effectLaneRowCount(sections: ReadonlyArray<EffectSection>): number {
  return sections.length ? Math.max(...packEffectRows(sections)) + 1 : 0;
}

/** Greedy display rows: a section takes the first row whose last section ended
 *  at or before its start. Stacking is array order, not rows. */
export function packEffectRows(sections: ReadonlyArray<EffectSection>): number[] {
  const rowEnds: number[] = [];
  const order = sections.map((s, i) => i).sort((a, b) => sections[a].start - sections[b].start || a - b);
  const rows = new Array<number>(sections.length);
  for (const i of order) {
    const s = sections[i];
    let row = rowEnds.findIndex((end) => end <= s.start + 1e-9);
    if (row === -1) { row = rowEnds.length; rowEnds.push(s.end); } else rowEnds[row] = s.end;
    rows[i] = row;
  }
  return rows;
}

export function moveSectionWindow(section: EffectSection, deltaSec: number, clipDuration: number) {
  const len = section.end - section.start;
  // Clamp first so the window fits, then snap; the duration is never changed.
  const maxStart = Math.max(0, clipDuration - len);
  const clamped = Math.min(maxStart, Math.max(0, section.start + deltaSec));
  let start = snapToExportFrame(clamped);
  if (start > maxStart + 1e-9) start = lastCompleteFrameTime(clamped);
  start = Math.max(0, start);
  // A window longer than the clip cannot keep its length inside it.
  return { start, end: len > clipDuration ? clipDuration : start + len };
}

export function trimSectionWindow(section: EffectSection, edge: 'start' | 'end', deltaSec: number, clipDuration: number) {
  if (edge === 'end') {
    const end = snapToExportFrame(Math.min(clipDuration, Math.max(section.start + FRAME_DURATION, section.end + deltaSec)));
    return { start: section.start, end: Math.max(end, section.start + FRAME_DURATION) };
  }
  const start = snapToExportFrame(Math.max(0, Math.min(section.end - FRAME_DURATION, section.start + deltaSec)));
  return { start: Math.min(start, section.end - FRAME_DURATION), end: section.end };
}

export function createSectionAtPlayhead({ effect, playhead, clipDuration, id }: {
  effect: EffectId; playhead: number; clipDuration: number; id: string;
}): EffectSection | null {
  if (!(clipDuration >= FRAME_DURATION)) return null;
  const end0 = Math.min(clipDuration, snapToExportFrame(Math.max(0, playhead)) + DEFAULT_SECTION_SECONDS);
  const start = Math.max(0, Math.min(snapToExportFrame(Math.max(0, playhead)), end0 - FRAME_DURATION));
  const knob = EFFECT_SECTION_KNOBS[effect];
  return {
    id,
    effect,
    start,
    end: end0,
    intensity: isDynamicEffect(effect) ? 80 : 100,
    ease: true,
    ...(knob ? { params: { [knob.key]: knob.default } } : {}),
  };
}
